import {FieldMoneyInput, InfoTooltip} from '@sollapay/ui/components';
import {Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {AiFlashWrapper} from '@/features/ai-extraction';
import {generatePurchasePriceNis, withDebugFieldChange} from '@/features/debug-test-data';

import type {useAiFillFlash, useFieldAiProps} from '@/features/ai-extraction';
import type {CreateBuyerFormValues} from '@/features/buyers/validation';
import type {FC} from 'react';
import type {UseFormReturn} from 'react-hook-form';

export interface PurchasePriceFieldProps {
  form: UseFormReturn<CreateBuyerFormValues>;
  fieldAi: ReturnType<typeof useFieldAiProps>;
  isFlashing: ReturnType<typeof useAiFillFlash>;
  className?: string;
}

export const PurchasePriceField: FC<PurchasePriceFieldProps> = ({
  form,
  fieldAi,
  isFlashing,
  className
}) => {
  const {t} = useTranslation('trustAccounts');
  const {
    control,
    formState: {errors}
  } = form;
  const prefix = 'view.buyers.addBuyerModal.assetDetails';
  // Named off the rendered label rather than a fixed string, so a deal type that relabels the
  // field (an assignment calls it תמורה כוללת) doesn't get told a different field is missing.
  const labelText = t(`${prefix}.purchasePrice`);
  const tooltip = t(`${prefix}.purchasePriceTooltip`, {defaultValue: ''});

  return (
    <AiFlashWrapper field="purchasePriceNis" fieldAi={fieldAi} isFlashing={isFlashing}>
      {ai => (
        <Controller
          name="purchasePriceNis"
          control={control}
          rules={{required: t('common:requiredNamed', {field: labelText})}}
          render={({field}) => (
            <FieldMoneyInput
              label={labelText}
              labelSuffix={tooltip ? <InfoTooltip content={tooltip} /> : undefined}
              placeholder={t(`${prefix}.purchasePricePlaceholder`)}
              error={errors.purchasePriceNis?.message}
              required
              value={field.value}
              onChange={value => {
                field.onChange(value);
                ai.onAiChange();
              }}
              onBlur={field.onBlur}
              cornerBadge={withDebugFieldChange(
                ai.aiIndicator,
                field.onChange,
                generatePurchasePriceNis,
                ai.onAiChange
              )}
              isLoading={ai.aiGenerating}
              className={className}
            />
          )}
        />
      )}
    </AiFlashWrapper>
  );
};
